/**
 * AmountsTab.js — Standalone payment-of-record surface.
 *
 * Lives independent of Patient Log so doctors who hide the amount column
 * from Patient Log (Configuration toggle) still get a private revenue
 * surface. Powered by /api/dashboard/amounts.
 */
import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { IndianRupee, Filter, Download, Calendar, Wallet, CreditCard, Loader2 } from 'lucide-react';

const SERVICE_LABEL = { consult: 'Consult', procedure: 'Procedure', surgery: 'Surgery' };
const MODE_LABEL = { cash: 'Cash', online: 'Online' };

export default function AmountsTab() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total: 0, cash: 0, online: 0 });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [serviceType, setServiceType] = useState('');
  const [paymentMode, setPaymentMode] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ days: String(days) });
    if (serviceType) params.set('service_type', serviceType);
    if (paymentMode) params.set('payment_mode', paymentMode);
    api.get(`/dashboard/amounts?${params}`)
      .then(r => {
        setRows(r.data?.rows || []);
        setTotals({ total: r.data?.total || 0, cash: r.data?.total_cash || 0, online: r.data?.total_online || 0 });
      })
      .catch(() => { setRows([]); setTotals({ total: 0, cash: 0, online: 0 }); })
      .finally(() => setLoading(false));
  }, [days, serviceType, paymentMode]);

  const exportCsv = () => {
    const head = ['Date', 'Time', 'Patient', 'Phone', 'Type', 'Mode', 'Amount (₹)'];
    const lines = [head.join(',')];
    rows.forEach(r => {
      const dt = r.created_at ? new Date(r.created_at) : null;
      lines.push([
        r.visit_date || (dt ? dt.toISOString().slice(0, 10) : ''),
        dt ? dt.toTimeString().slice(0, 5) : '',
        `"${(r.patient_name || '').replace(/"/g, '""')}"`,
        r.patient_phone_masked || '',
        SERVICE_LABEL[r.service_type] || r.service_type || '',
        MODE_LABEL[r.payment_mode] || r.payment_mode || '',
        r.amount_paid || 0,
      ].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `amounts-${days}d-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const todayTotal = rows.filter(r => (r.visit_date || (r.created_at || '').slice(0, 10)) === today)
                         .reduce((s, r) => s + (r.amount_paid || 0), 0);

  return (
    <div className="space-y-4" data-testid="amounts-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Amounts</h3>
          <p className="text-xs text-slate-500 mt-0.5">Every paid consult, procedure or surgery — independent of what's visible on your Patient Log.</p>
        </div>
        <button
          onClick={exportCsv}
          disabled={!rows.length}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold disabled:opacity-40"
          data-testid="amounts-export-csv"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard testId="amounts-total" icon={<IndianRupee size={14} />} label={`Total · last ${days}d`} value={`₹${totals.total.toLocaleString('en-IN')}`} accent="sky" />
        <SummaryCard testId="amounts-today" icon={<Calendar size={14} />} label="Today" value={`₹${todayTotal.toLocaleString('en-IN')}`} accent="emerald" />
        <SummaryCard testId="amounts-cash" icon={<Wallet size={14} />} label="Cash" value={`₹${totals.cash.toLocaleString('en-IN')}`} accent="amber" />
        <SummaryCard testId="amounts-online" icon={<CreditCard size={14} />} label="Online" value={`₹${totals.online.toLocaleString('en-IN')}`} accent="violet" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-white rounded-2xl border border-slate-200 p-3" data-testid="amounts-filters">
        <Filter size={13} className="text-slate-400" />
        <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="text-xs rounded-lg border border-slate-200 px-2 py-1.5 bg-white" data-testid="amounts-filter-days">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 12 months</option>
        </select>
        <select value={serviceType} onChange={(e) => setServiceType(e.target.value)} className="text-xs rounded-lg border border-slate-200 px-2 py-1.5 bg-white" data-testid="amounts-filter-service">
          <option value="">All types</option>
          <option value="consult">Consult</option>
          <option value="procedure">Procedure</option>
          <option value="surgery">Surgery</option>
        </select>
        <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} className="text-xs rounded-lg border border-slate-200 px-2 py-1.5 bg-white" data-testid="amounts-filter-mode">
          <option value="">All modes</option>
          <option value="cash">Cash</option>
          <option value="online">Online</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="amounts-table-wrap">
        {loading ? (
          <div className="px-6 py-10 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading paid visits…
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-slate-500" data-testid="amounts-empty">
            No paid visits in this window. Add an amount when accepting a check-in or logging a visit.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="amounts-table">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2.5">Date</th>
                  <th className="text-left px-3 py-2.5">Time</th>
                  <th className="text-left px-3 py-2.5">Patient</th>
                  <th className="text-left px-3 py-2.5">Type</th>
                  <th className="text-left px-3 py-2.5">Mode</th>
                  <th className="text-right px-3 py-2.5">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(r => {
                  const dt = r.created_at ? new Date(r.created_at) : null;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`amounts-row-${r.id}`}>
                      <td className="px-3 py-2.5">{r.visit_date || (dt ? dt.toISOString().slice(0, 10) : '—')}</td>
                      <td className="px-3 py-2.5 text-slate-500">{dt ? dt.toTimeString().slice(0, 5) : '—'}</td>
                      <td className="px-3 py-2.5">
                        <p className="font-medium text-slate-900">{r.patient_name || '—'}</p>
                        <p className="text-[10px] text-slate-400">{r.patient_phone_masked}</p>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-[10px] font-semibold border border-sky-200">
                          {SERVICE_LABEL[r.service_type] || r.service_type || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${r.payment_mode === 'online' ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                          {MODE_LABEL[r.payment_mode] || 'Cash'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-semibold text-slate-900">₹{(r.amount_paid || 0).toLocaleString('en-IN')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, accent, testId }) {
  const accents = {
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return (
    <div className={`rounded-2xl border p-3 ${accents[accent] || accents.sky}`} data-testid={testId}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold opacity-80">
        {icon}<span>{label}</span>
      </div>
      <p className="text-xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
    </div>
  );
}
